export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  status: 'sent' | 'pending' | 'error' | 'timeout';
}

export interface ChatSession {
  sessionId: string | null;
  messages: ChatMessage[];
  isProcessing: boolean;
}

export interface ChatRequest {
  message: string;
  sessionId: string;
  agentId?: string;
  modelId?: string;
}

export interface ChatAttachment {
  name: string;
  size: number;
  text?: string;
}

export interface ChatResponse {
  response: string;
  sessionId: string;
  timestamp: string;
}

export interface ModelOption {
  id: string;
  label: string;
  sublabel: string;
}

export interface SendMessagePayload {
  text: string;
  files?: ChatAttachment[];
}

export interface ConnectedEvent {
  type: 'connected';
  connectionId: string;
  sessionId: string;
  agentId: string;
  isNewSession: boolean;
  historyAvailable: boolean;
}

export interface AckEvent {
  type: 'ack';
  turnId: string;
  timestamp: string;
}

export interface TokenEvent {
  type: 'token';
  turnId: string;
  content: string;
  sequence: number;
  timestamp: string;
}

export interface CompleteEvent {
  type: 'complete';
  turnId: string;
  timestamp: string;
}

export type ReasoningEventType = 'tool_call' | 'thinking_step' | 'tool_result' | 'error';

export interface ReasoningEvent {
  type: 'reasoning_event';
  turnId: string;
  workItemId?: string;
  eventType: ReasoningEventType;
  label: string;
  toolName?: string;
  detail?: string;
  timestamp: string;
}

export interface ErrorEvent {
  type: 'error';
  code: ArafErrorCode;
  message: string;
  turnId: string | null;
  timestamp: string;
}

export interface TokenExpiringEvent {
  type: 'token_expiring';
  expiresIn: number;
  timestamp: string;
}

export interface TaskSubscribedEvent {
  type: 'task_subscribed';
  workItemId: string;
  hasActiveExecution: boolean;
  timestamp: string;
}

export interface TaskUnsubscribedEvent {
  type: 'task_unsubscribed';
  workItemId: string;
  timestamp: string;
}

export interface TaskCompleteEvent {
  type: 'task_complete';
  workItemId: string;
  timestamp: string;
}

export interface TaskHistoryItem {
  turnId: string;
  eventType: ReasoningEventType;
  label: string;
  toolName?: string;
  timestamp: string;
}

export interface TaskHistoryEvent {
  type: 'task_history';
  workItemId: string;
  events: TaskHistoryItem[];
  timestamp: string;
}

export interface TaskResumedEvent {
  type: 'task_resumed';
  workItemId: string;
  timestamp: string;
}

export interface TaskCancelledEvent {
  type: 'task_cancelled';
  workItemId: string;
  timestamp: string;
}

export interface AgentQueryEvent {
  type: 'agent_query';
  queryId: string;
  workItemId: string;
  agentId: string;
  question: string;
  context?: string;
  timestamp: string;
}

export interface TaskDispatchedEvent {
  type: 'task_dispatched';
  workItemId: string;
  agentId: string;
  sessionId: string;
  timestamp: string;
}

export interface ActiveWorkerSnapshot {
  workerId: string;
  workItemId: number;
  agentIdentifier: string;
  taskWorkItemId?: string;
  startedAt: string;
  status: 'active' | 'stalled';
}

export interface QueueUpdatedEvent {
  type: 'queue_updated';
  queueId: string;
  event: 'run_started' | 'run_completed' | 'run_failed' | 'worker_changed';
  activeWorkers?: ActiveWorkerSnapshot[];
  timestamp: string;
}

export interface BullMQJobProgressData {
  type: 'tool_call' | 'thinking_step' | 'tool_result' | 'error' | 'token_update';
  label: string;
  toolName?: string;
  detail?: string;
  tokens?: { inputTokens: number; cumulativeOutputTokens: number };
}

export interface BullMQJobWaitingEvent {
  queueName: string;
  jobId: string;
  timestamp: string;
}

export interface BullMQJobActiveEvent {
  queueName: string;
  jobId: string;
  prev: string;
  timestamp: string;
}

export interface BullMQJobProgressEvent {
  queueName: string;
  jobId: string;
  data: BullMQJobProgressData;
  timestamp: string;
}

export interface BullMQJobCompletedEvent {
  queueName: string;
  jobId: string;
  returnvalue: { workItemId: number; durationSec: number; exitCode: number } | null;
  timestamp: string;
}

export interface BullMQJobFailedEvent {
  queueName: string;
  jobId: string;
  failedReason: string;
  timestamp: string;
}

export interface BullMQJobStalledEvent {
  queueName: string;
  jobId: string;
  timestamp: string;
}

export interface BullMQJobDelayedEvent {
  queueName: string;
  jobId: string;
  delay: number;
  timestamp: string;
}

export interface AgentReplyPayload {
  type: 'agent_reply';
  queryId: string;
  workItemId: string;
  reply: string;
}

export interface CancelTaskPayload {
  type: 'cancel_task';
  workItemId: string;
  reason?: string;
}

export interface SessionsListItem {
  sessionId: string;
  title: string;
  lastActiveAt: string;
  createdAt: string;
  messageCount: number;
}

export interface SessionsListEvent {
  type: 'sessions_list';
  sessions: SessionsListItem[];
  nextCursor: string | null;
  timestamp: string;
}

export interface SessionHistoryMessage {
  turnId: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface SessionHistoryEvent {
  type: 'session_history';
  sessionId: string;
  messages: SessionHistoryMessage[];
  timestamp: string;
}

export interface MessagePayload {
  type: 'message';
  sessionId: string;
  content: string;
  agentId?: string;
  modelId?: string;
  systemPrompt?: string;
  attachments?: Array<{
    fileName: string;
    content: string;
    mimeType: string;
  }>;
}

export interface SubscribeTaskPayload {
  type: 'subscribe_task';
  workItemId: string;
}

export interface UnsubscribeTaskPayload {
  type: 'unsubscribe_task';
  workItemId: string;
}

export interface GetTaskHistoryPayload {
  type: 'get_task_history';
  workItemId: string;
}

export interface GetSessionHistoryPayload {
  type: 'get_session_history';
  sessionId: string;
}

export interface ListSessionsPayload {
  type: 'list_sessions';
  limit?: number;
  cursor?: string;
}

export type ArafErrorCode =
  | 'authentication_failed'
  | 'session_expired'
  | 'forbidden'
  | 'not_found'
  | 'turn_in_progress'
  | 'empty_message'
  | 'validation_error'
  | 'execution_failed'
  | 'dispatch_failed'
  | 'server_shutdown'
  | 'capacity_exceeded'
  | 'insufficient_credits';

export const ERROR_CODE_MESSAGES: Record<ArafErrorCode, string> = {
  authentication_failed: 'Authentication failed. Please sign in again.',
  session_expired: 'Your session has expired. Starting a new session.',
  forbidden: 'You do not have permission to access this resource.',
  not_found: 'The requested resource was not found.',
  turn_in_progress: 'Please wait for the current response to finish.',
  empty_message: 'Message cannot be empty.',
  validation_error: 'Invalid message format.',
  execution_failed: 'The agent encountered an error. Please try again.',
  dispatch_failed: 'Failed to dispatch task. Please try again.',
  server_shutdown: 'Server is restarting. Reconnecting…',
  capacity_exceeded: 'Server is at capacity. Please try again shortly.',
  insufficient_credits: 'LLM provider has insufficient credits. Please top up your account.',
};

export interface ArafServerToClientEvents {
  connected: (data: ConnectedEvent) => void;
  ack: (data: AckEvent) => void;
  token: (data: TokenEvent) => void;
  complete: (data: CompleteEvent) => void;
  reasoning_event: (data: ReasoningEvent) => void;
  error: (data: ErrorEvent) => void;
  token_expiring: (data: TokenExpiringEvent) => void;
  task_subscribed: (data: TaskSubscribedEvent) => void;
  task_unsubscribed: (data: TaskUnsubscribedEvent) => void;
  task_complete: (data: TaskCompleteEvent) => void;
  task_history: (data: TaskHistoryEvent) => void;
  sessions_list: (data: SessionsListEvent) => void;
  session_history: (data: SessionHistoryEvent) => void;
  agent_query: (data: AgentQueryEvent) => void;
  task_dispatched: (data: TaskDispatchedEvent) => void;
  task_resumed: (data: TaskResumedEvent) => void;
  task_cancelled: (data: TaskCancelledEvent) => void;
  queue_updated: (data: QueueUpdatedEvent) => void;
  bullmq_job_waiting: (data: BullMQJobWaitingEvent) => void;
  bullmq_job_active: (data: BullMQJobActiveEvent) => void;
  bullmq_job_progress: (data: BullMQJobProgressEvent) => void;
  bullmq_job_completed: (data: BullMQJobCompletedEvent) => void;
  bullmq_job_failed: (data: BullMQJobFailedEvent) => void;
  bullmq_job_stalled: (data: BullMQJobStalledEvent) => void;
  bullmq_job_delayed: (data: BullMQJobDelayedEvent) => void;
  bullmq_queue_skipped: (data: { queueName: string; jobId: string; workItemId?: number }) => void;
  bullmq_queue_concurrency: (data: { slug: string; concurrency: number }) => void;
}

export interface ArafClientToServerEvents {
  message: (data: MessagePayload) => void;
  subscribe_task: (data: SubscribeTaskPayload) => void;
  unsubscribe_task: (data: UnsubscribeTaskPayload) => void;
  get_task_history: (data: GetTaskHistoryPayload) => void;
  get_session_history: (data: GetSessionHistoryPayload) => void;
  list_sessions: (data: ListSessionsPayload) => void;
  agent_reply: (data: AgentReplyPayload) => void;
  cancel_task: (data: CancelTaskPayload) => void;
}
